import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuthStore } from './stores/authStore'
import { supabase } from './services/supabase'
import './i18n' // Initialize i18n

// Layout components
import Layout from './components/Layout'
import AuthLayout from './components/AuthLayout'

// Page components
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import SignUpPage from './pages/SignUpPage'
import DashboardPage from './pages/DashboardPage'
import GamePage from './pages/GamePage'
import StoryPage from './pages/StoryPage'
import ProfilePage from './pages/ProfilePage'
import LeaderboardPage from './pages/LeaderboardPage'
import SettingsPage from './pages/SettingsPage'
import WordsPage from './pages/WordsPage'
import PronunciationPracticePage from './pages/PronunciationPracticePage'
import AdminPage from './pages/AdminPage'
import SpeechAnalysisTestPage from './pages/SpeechAnalysisTestPage'

// Loading and error components
import LoadingSpinner from './components/ui/LoadingSpinner'
import ErrorBoundary from './components/ErrorBoundary'

function App() {
  const { user, profile, setUser, setLoading, loading } = useAuthStore()

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        if (error) {
          console.error('Error getting session:', error)
        } else {
          setUser(session?.user ?? null)
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error)
      } finally {
        setLoading(false)
      }
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id)
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={
              user ? <Navigate to={"/dashboard"} /> : <HomePage />
            }  />

          {/* Auth routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
          </Route>

          {/* Protected routes */}
          <Route element={<Layout />}>
            <Route path="/dashboard" element={
              user ? <DashboardPage /> : <Navigate to="/login" replace />
            } />
            <Route path="/words" element={
              user ? <WordsPage /> : <Navigate to="/login" replace />
            } />
            <Route path="/word/:wordId" element={
              user ? <PronunciationPracticePage /> : <Navigate to="/login" replace />
            } />
            <Route path="/game" element={
              user ? <GamePage /> : <Navigate to="/login" replace />
            } />
            <Route path="/game/:wordId" element={
              user ? <GamePage /> : <Navigate to="/login" replace />
            } />
            <Route path="/stories" element={
              user ? <StoryPage /> : <Navigate to="/login" replace />
            } />
            <Route path="/story/:storyId" element={
              user ? <StoryPage /> : <Navigate to="/login" replace />
            } />
            <Route path="/profile" element={
              user ? <ProfilePage /> : <Navigate to="/login" replace />
            } />
            <Route path="/leaderboard" element={
              user ? <LeaderboardPage /> : <Navigate to="/login" replace />
            } />
            <Route path="/settings" element={
              user ? <SettingsPage /> : <Navigate to="/login" replace />
            } />
            <Route path="/admin" element={
              profile && profile.is_admin ? <AdminPage /> : <Navigate to="/" replace />
            } />
            <Route path="/speech-test" element={
              user && profile?.is_admin ? <SpeechAnalysisTestPage /> : <Navigate to="/login" replace />
            } />
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </ErrorBoundary>
  )
}

export default App